import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input, NgZone,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { FocusMonitor } from '@angular/cdk/a11y';
import { BaseFieldDirective } from '../base-field.directive';
import { AutoCompleteField } from './AutoCompleteField';
import { of } from 'rxjs';
import { catchError, debounceTime, filter, startWith, switchMap, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ApiResult, ListaPaginada, Meta } from '../../../services';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AutoCompleteService } from './auto-complete.service';
import { isNotNullOrUndefined, isNotNullOrUndefinedOrEmpty } from "../../../helpers";

@Component({
    selector: 'ui-auto-complete-field',
    templateUrl: './auto-complete-field.component.html',
    styleUrls: ['./auto-complete-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AutoCompleteFieldComponent extends BaseFieldDirective
    implements OnDestroy, OnInit {

    @Input() config!: AutoCompleteField;
    @ViewChild('searchInput', { static: false }) searchInput: ElementRef<HTMLInputElement>;
    values = [];
    selecionado?: any;
    meta!: Meta;
    carregando = true;
    entityValue = 'id';
    entityText = 'nome'

    constructor(
        @Inject(AutoCompleteService) protected service: AutoCompleteService,
        protected httpClient: HttpClient,
        protected fmRef: FocusMonitor,
        protected elRef: ElementRef<HTMLElement>,
        protected changeDetector: ChangeDetectorRef,
        protected ngZone: NgZone
    ) {
        super(fmRef, elRef, changeDetector, ngZone);
    }

    getForm(): UntypedFormControl {
        const form = this.formGroup!.get(this.config.property)!;
        return form.get(this.entityText) as UntypedFormControl;
    }

    ngOnInit() {
        super.ngOnInit();

        if(isNotNullOrUndefinedOrEmpty(this.config.entityValue)){
            this.entityValue = this.config.entityValue;
        }

        if(isNotNullOrUndefinedOrEmpty(this.config.entityText)){
            this.entityText = this.config.entityText;
        }

        this.selecionado = null;
        if (isNotNullOrUndefinedOrEmpty(this.controle.value?.id)) {
            if(this.config.usaValueObject) {
                this.selecionado = {};
                this.selecionado[this.entityText] =  this.controle.value[this.entityText];
                this.selecionado[this.entityValue] = this.controle.value[this.entityValue]
                this.selecionado[this.config.property] = this.controle.value;
            }else{
                this.selecionado = this.mapToAutocompleteItem(this.controle.value[this.config.property]);
            }
        }


        const fc = this.getForm()!;

        // Formulário limpo por fora (ex.: reset depois de salvar): o chip mostra "selecionado", que
        // precisa acompanhar o controle vazio.
        this.subscriptions.push(
            this.controle.valueChanges
                .pipe(filter(valor => this.selecionado !== null && !isNotNullOrUndefinedOrEmpty(valor?.[this.entityValue])))
                .subscribe(() => {
                    this.selecionado = null;
                    this.values = [];
                    this.changeDetector.markForCheck();
                }));

        this.subscriptions.push(
            fc.valueChanges
                .pipe(startWith(''),
                    debounceTime(500),
                    filter(() => fc.touched || fc.dirty),
                    filter((valor) => !isNotNullOrUndefinedOrEmpty(this.selecionado) || valor !== this.selecionado[this.entityText]),
                    tap(() => this.controle.get(this.entityValue)?.setValue('')),
                    switchMap(this.getItens))
                .subscribe());
    }

    public atualizaControle = (event: MatAutocompleteSelectedEvent) => {
        this.selecionado = event.option.value;

        this.controle.patchValue(event.option.value);

        if (this.config.onValueSelectedFunc) {
            this.config.onValueSelectedFunc(event.option.value);
        }

        this.searchInput.nativeElement.value = '';
        this.values = [];
    };

    public getValue = () => {
        let valor = isNotNullOrUndefinedOrEmpty(this.selecionado) && this.selecionado[this.entityText] ? this.selecionado[this.entityText] : '';
        return valor.length > 50 ? `${valor.substr(0, 50)}...` : valor;
    }

    private getItens = (filtro: any) => {
        if (!filtro?.length || filtro?.length < 2) {
            this.carregando = false;
            this.values = [];
            return of([]);
        }

        const filtroAutoComplete = this.config.propertySearchFunc ?
            this.config.propertySearchFunc(filtro) :
            `${this.config.propertySearch}.ToLower().Contains("${filtro.toLowerCase().trim()}")`;
        const ordemAutoComplete = this.config.propertyOrderFunc ?
            this.config.propertyOrderFunc(filtro) : this.config.propertyOrder;
        this.carregando = true;
        this.changeDetector.detectChanges();
        return this.getRequest(filtroAutoComplete, ordemAutoComplete);
    };

    private getRequest = (filtroAutoComplete: string, ordemAutoComplete: string) =>
        this.httpClient.get<ApiResult<ListaPaginada<any>>>(this.montaUrl(filtroAutoComplete, ordemAutoComplete))
        .pipe(tap(({ data }) => {

            this.meta = { pagina: data!.pagina, total: data!.total } as Meta;

            if (!data!.total) {
                this.controle.get(this.entityValue)!.reset();
            }

            this.controle.updateValueAndValidity();

            this.values = data!.itens.map(item => this.mapToAutocompleteItem(item));
            this.carregando = false;
            this.changeDetector.detectChanges();
        }), catchError((error, source) => {
            this.carregando = false;
            return of([]);
        }));

    private montaUrl = (filtro: string, ordem: string) => {
        let url = `${this.config.url || this.service.apiUrl}${this.config.endpoint}`;

        if(url.indexOf('?') === -1){
            url += '?';
        }else{
            url += '&';
        }

        url += `filtro=${encodeURIComponent(filtro)}`;

        if(isNotNullOrUndefined(ordem) && ordem != "") {
            url += `&ordenacao=${ordem}`;
        }
        return url;
    }

    private mapToAutocompleteItem = (item: any) => {
        let value = {};

        value[this.entityText] = this.config.entityTextFunc ? this.config.entityTextFunc(item) : item[this.entityText];
        value[this.entityValue] = item[this.entityValue]
        value[this.config.property] = item;
        return value;
    };

    limpar(event: any) {
        this.controle.reset();
        this.controle.markAsTouched();

        if (this.config.onValueRemovedFunc) {
            this.config.onValueRemovedFunc(event);
        }

        this.selecionado = null;
        this.values = [];
    }

    onBackspace(event: any) {
        if (event.key === 'Backspace') {
            this.values = [];
        }
    }
}
